import { useMemo } from 'react'

import {
  SEGMENTED_TAB_VARIANTS,
  SegmentedTabs,
} from '#/shared/ui/segmented-tabs'

import type { CampaignTab } from '../model/use-campaign-page'
import type { CampaignTabKey } from '../model/tabs'

interface CampaignStatusTabsProps {
  tabs: CampaignTab[]
  activeTab: CampaignTabKey
  onTabChange: (tab: CampaignTabKey) => void
}

/**
 * Status + ownership filters. Built on the shared segmented tabs so the fill
 * slides between tabs the way it does everywhere else in the app; every label
 * keeps the same weight, and only the colour changes on selection — a weight
 * swap would reflow the strip mid-slide.
 */
export function CampaignStatusTabs({
  tabs,
  activeTab,
  onTabChange,
}: CampaignStatusTabsProps) {
  const items = useMemo(
    () =>
      tabs.map((tab) => ({
        value: tab.key,
        render: () => (
          <>
            {tab.label}
            <span
              className={
                tab.key === activeTab ? 'text-gray-500' : 'text-gray-300'
              }
            >
              {tab.count}
            </span>
          </>
        ),
      })),
    [tabs, activeTab],
  )

  // The strip scrolls rather than wraps on narrow screens: the sliding fill is
  // absolutely positioned by `left`/`width` alone, so a second row would strand
  // it. The scroll container has to sit outside the track for the same reason.
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <SegmentedTabs
        items={items}
        value={activeTab}
        onValueChange={onTabChange}
        variant={SEGMENTED_TAB_VARIANTS.SOFT}
        className="w-max"
      />
    </div>
  )
}
